import { useEffect, useState } from "react";
import {
  Factory, Store, ShoppingBag, CheckCircle2, Clock, XCircle, Package,
  Eye, MessageCircle, IndianRupee, TrendingUp, HardDrive,
} from "lucide-react";
import api from "@/lib/api";
import { rupee } from "@/lib/format";

const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), u.length - 1);
  return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

function Card({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border rounded-xl p-4">
      <div className={`h-9 w-9 rounded-lg grid place-items-center ${color}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-heading text-2xl font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground font-semibold">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-muted-foreground">Loading…</div>;
  const r = d.revenue;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">Admin Dashboard</h1>
        <p className="text-muted-foreground text-sm font-medium">Complete platform overview.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Factory} label="Manufacturers" value={d.total_manufacturers} color="bg-amber-100 text-amber-700" />
        <Card icon={Store} label="Wholesalers" value={d.total_wholesalers} color="bg-blue-100 text-blue-700" />
        <Card icon={ShoppingBag} label="Retailers" value={d.total_retailers} color="bg-purple-100 text-purple-700" />
        <Card icon={CheckCircle2} label="Active Subscriptions" value={d.active_subscriptions} color="bg-emerald-100 text-emerald-700" />
        <Card icon={Clock} label="Expiring Soon" value={d.expiring_subscriptions} color="bg-amber-100 text-amber-700" />
        <Card icon={XCircle} label="Expired" value={d.expired_subscriptions} color="bg-rose-100 text-rose-700" />
        <Card icon={Package} label="Active Products" value={d.active_products} color="bg-slate-100 text-slate-700" />
        <Card icon={Clock} label="Pending Products" value={d.pending_products} color="bg-amber-100 text-amber-700" />
        <Card icon={Eye} label="Product Views" value={d.product_views} color="bg-blue-100 text-blue-700" />
        <Card icon={MessageCircle} label="WhatsApp Clicks" value={d.whatsapp_clicks} color="bg-green-100 text-green-700" />
        <Card icon={Clock} label="Pending Payments" value={d.pending_payments} color="bg-rose-100 text-rose-700" />
        <div className="bg-card border rounded-xl p-4" data-testid="storage-used-card">
          <div className="flex items-center justify-between">
            <div className="h-9 w-9 rounded-lg grid place-items-center bg-indigo-100 text-indigo-700"><HardDrive className="h-5 w-5" /></div>
            {(() => { const q = d.storage_quota_bytes || 0; const pct = q ? Math.min(100, (d.storage_used_bytes / q) * 100) : 0; return <span className={`text-xs font-extrabold ${pct >= 90 ? "text-rose-600" : pct >= 70 ? "text-amber-600" : "text-emerald-600"}`}>{pct.toFixed(pct < 10 ? 1 : 0)}%</span>; })()}
          </div>
          <div className="mt-3 font-heading text-2xl font-extrabold">{fmtBytes(d.storage_used_bytes)}</div>
          <div className="text-xs text-muted-foreground font-semibold">Storage Used · {d.storage_files || 0} files</div>
          {(() => {
            const q = d.storage_quota_bytes || 0;
            const pct = q ? Math.min(100, (d.storage_used_bytes / q) * 100) : 0;
            const remaining = Math.max(0, q - (d.storage_used_bytes || 0));
            return (
              <div className="mt-3">
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div data-testid="storage-bar" className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[11px] text-muted-foreground font-semibold mt-1.5">{fmtBytes(remaining)} free of {fmtBytes(q)}</div>
              </div>
            );
          })()}
        </div>
      </div>

      <div>
        <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /> Revenue Reporting</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            ["Subscriptions", r.subscription],
            ["Renewals", r.renewal],
            ["Additional Slots", r.slot],
          ].map(([l, v]) => (
            <div key={l} className="bg-card border rounded-xl p-4">
              <div className="text-xs text-muted-foreground font-semibold">{l}</div>
              <div className="font-heading text-xl font-extrabold mt-1">{rupee(v)}</div>
            </div>
          ))}
          <div className="bg-slate-900 text-white rounded-xl p-4">
            <div className="text-xs text-white/60 font-semibold flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Total Revenue</div>
            <div className="font-heading text-xl font-extrabold mt-1 text-primary">{rupee(r.total)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
